import {Box, Paper, Typography} from '@mui/material';
import React, {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {TeamSessionsReportRow} from '../../interfaces';
import {getTeamSessionsReport} from '../../api/reportAPI';
import {DataGrid, GridColDef} from '@mui/x-data-grid';
import {downloadReportCsv} from '../../services/reportService';
import TeamSessionsFilter from './TeamSessionsFilter';
import {DateTime} from 'luxon';
import FullScreenLoadingSpinner from '../shared/FullScreenLoading';
import {TFunction} from 'i18next';
import {getTime} from '../../services/dateService';
import {formatNumber, formatPercent} from '../../services/mathService';

// Converts milliseconds to formatted duration string using getTime
const formatDuration = (durationMs: number | null | undefined): string => {
  if (!durationMs) return '-';
  const totalSeconds = Math.floor(durationMs / 1000);
  return getTime(totalSeconds) ?? '-';
};

const getColumns = (t: TFunction): GridColDef[] => {
  return [
    {
      field: 'sessionId',
      headerName: t('teamSessionsReport.sessionId') as string,
      width: 100,
      filterable: true,
    },
    {
      field: 'startTime',
      headerName: t('sessionStats.startTime') as string,
      width: 160,
      filterable: true,
      renderCell: params => {
        const localDisplayStartTime = params.row?.localDisplayStartTime;
        if (localDisplayStartTime) return localDisplayStartTime;

        const startTime = params.row?.startTime ?? params.value;
        return startTime
          ? DateTime.fromISO(startTime as unknown as string).toFormat(
              'M/d/yyyy h:mm a',
            )
          : '-';
      },
    },
    {
      field: 'duration',
      headerName: t('teamSessionsReport.duration') as string,
      width: 100,
      filterable: true,
    },
    {
      field: 'sessionType',
      headerName: t('sessionStats.sessionType') as string,
      width: 100,
      filterable: true,
    },
    {
      field: 'activePlayerCount',
      headerName: t('teamSessionsReport.activePlayers') as string,
      width: 120,
      filterable: true,
    },
    // Team Avg Load columns
    {
      field: 'teamTotalLoad',
      headerName: t('teamSessionsReport.teamTotalLoad') as string,
      width: 140,
      filterable: true,
    },
    {
      field: 'teamAvgLoadTeam',
      headerName: t('teamSessionsReport.teamAvgLoadTeam') as string,
      width: 140,
      filterable: true,
    },
    {
      field: 'teamAvgLoadForward',
      headerName: t('teamSessionsReport.teamAvgLoadForward') as string,
      width: 140,
      filterable: true,
    },
    {
      field: 'teamAvgLoadDefense',
      headerName: t('teamSessionsReport.teamAvgLoadDefense') as string,
      width: 140,
      filterable: true,
    },
    // Energy Ratio columns
    {
      field: 'energyRatioTeam',
      headerName: t('teamSessionsReport.energyRatioTeam') as string,
      width: 130,
      filterable: true,
    },
    {
      field: 'energyRatioForward',
      headerName: t('teamSessionsReport.energyRatioForward') as string,
      width: 140,
      filterable: true,
    },
    {
      field: 'energyRatioDefense',
      headerName: t('teamSessionsReport.energyRatioDefense') as string,
      width: 140,
      filterable: true,
    },
    // Practice Intensity columns
    {
      field: 'practiceIntensityTeam',
      headerName: t('teamSessionsReport.practiceIntensityTeam') as string,
      width: 150,
      filterable: true,
    },
    {
      field: 'practiceIntensityForward',
      headerName: t('teamSessionsReport.practiceIntensityForward') as string,
      width: 160,
      filterable: true,
    },
    {
      field: 'practiceIntensityDefense',
      headerName: t('teamSessionsReport.practiceIntensityDefense') as string,
      width: 160,
      filterable: true,
    },
    // Hustle Score columns
    {
      field: 'avgHustleScoreTeam',
      headerName: t('teamSessionsReport.hustleScoreTeam') as string,
      width: 140,
      filterable: true,
    },
    {
      field: 'avgHustleScoreForward',
      headerName: t('teamSessionsReport.hustleScoreForward') as string,
      width: 150,
      filterable: true,
    },
    {
      field: 'avgHustleScoreDefense',
      headerName: t('teamSessionsReport.hustleScoreDefense') as string,
      width: 150,
      filterable: true,
    },
  ];
};

interface Props {
  teamId: number;
  teamName: string;
}

const TeamSessionsReport: React.FC<Props> = ({teamId, teamName}) => {
  const {t} = useTranslation();

  const [reportData, setReportData] = useState<TeamSessionsReportRow[] | null>(
    null,
  );
  const [loading, setLoading] = useState(false);

  const columns = useMemo(() => getColumns(t), [t]);

  const onSubmit = useCallback(
    async ({
      startDate,
      endDate,
      sessionTypes,
    }: {
      startDate: Date;
      endDate: Date;
      sessionTypes: string[];
    }) => {
      setLoading(true);
      try {
        const response = await getTeamSessionsReport({
          teamId,
          startDate,
          endDate,
          sessionTypes: sessionTypes.length > 0 ? sessionTypes : undefined,
        });
        setReportData(response ?? []);
      } catch (error) {
        console.error('Error fetching team sessions report:', error);
        setReportData([]);
      }
      setLoading(false);
    },
    [teamId],
  );

  const onReset = useCallback(() => {
    setReportData(null);
  }, []);

  // Transform data for DataGrid display
  const gridRows = useMemo(() => {
    if (!reportData) return [];
    return reportData.map(row => ({
      id: row.sessionId,
      sessionId: row.sessionId,
      localDisplayStartTime: row.localDisplayStartTime,
      startTime: row.startTime,
      duration: formatDuration(row.duration),
      sessionType: row.sessionType ?? '-',
      activePlayerCount: row.activePlayerCount ?? '-',
      teamTotalLoad: formatNumber(
        row.teamTotalLoad ? row.teamTotalLoad / 100 : row.teamTotalLoad,
        0,
      ),
      teamAvgLoadTeam: formatNumber(
        row.teamAvgLoad?.teamAvg
          ? row.teamAvgLoad?.teamAvg / 100
          : row.teamAvgLoad?.teamAvg,
        1,
      ),
      teamAvgLoadForward: formatNumber(
        row.teamAvgLoad?.forwardAvg
          ? row.teamAvgLoad?.forwardAvg / 100
          : row.teamAvgLoad?.forwardAvg,
        1,
      ),
      teamAvgLoadDefense: formatNumber(
        row.teamAvgLoad?.defenseAvg
          ? row.teamAvgLoad?.defenseAvg / 100
          : row.teamAvgLoad?.defenseAvg,
        1,
      ),
      energyRatioTeam: formatPercent(row.energyRatio?.teamAvg, 1),
      energyRatioForward: formatPercent(row.energyRatio?.forwardAvg, 1),
      energyRatioDefense: formatPercent(row.energyRatio?.defenseAvg, 1),
      practiceIntensityTeam: formatPercent(row.practiceIntensity?.teamAvg, 1),
      practiceIntensityForward: formatPercent(
        row.practiceIntensity?.forwardAvg,
        1,
      ),
      practiceIntensityDefense: formatPercent(
        row.practiceIntensity?.defenseAvg,
        1,
      ),
      avgHustleScoreTeam: formatNumber(row.avgHustleScore?.teamAvg, 1),
      avgHustleScoreForward: formatNumber(row.avgHustleScore?.forwardAvg, 1),
      avgHustleScoreDefense: formatNumber(row.avgHustleScore?.defenseAvg, 1),
    }));
  }, [reportData]);

  const onDownload = useCallback(() => {
    if (!gridRows) return;

    const exportReportData = gridRows.map(row => ({
      [t('teamSessionsReport.sessionId')]: row.sessionId,
      [t('sessionStats.startTime')]:
        row.localDisplayStartTime ??
        DateTime.fromISO(row.startTime as unknown as string).toFormat(
          'M/d/yyyy h:mm a',
        ),
      [t('teamSessionsReport.duration')]: row.duration,
      [t('sessionStats.sessionType')]: row.sessionType ?? '',
      [t('teamSessionsReport.activePlayers')]: row.activePlayerCount ?? '',
      [t('teamSessionsReport.teamTotalLoad')]: row.teamTotalLoad,
      [t('teamSessionsReport.teamAvgLoadTeam')]: row.teamAvgLoadTeam,
      [t('teamSessionsReport.teamAvgLoadForward')]: row.teamAvgLoadForward,
      [t('teamSessionsReport.teamAvgLoadDefense')]: row.teamAvgLoadDefense,
      [t('teamSessionsReport.energyRatioTeam')]: row.energyRatioTeam,
      [t('teamSessionsReport.energyRatioForward')]: row.energyRatioForward,
      [t('teamSessionsReport.energyRatioDefense')]: row.energyRatioDefense,
      [t('teamSessionsReport.practiceIntensityTeam')]:
        row.practiceIntensityTeam,
      [t('teamSessionsReport.practiceIntensityForward')]:
        row.practiceIntensityForward,
      [t('teamSessionsReport.practiceIntensityDefense')]:
        row.practiceIntensityDefense,
      [t('teamSessionsReport.hustleScoreTeam')]: row.avgHustleScoreTeam,
      [t('teamSessionsReport.hustleScoreForward')]: row.avgHustleScoreForward,
      [t('teamSessionsReport.hustleScoreDefense')]: row.avgHustleScoreDefense,
    }));

    downloadReportCsv({
      exportReportData,
      teamName,
      reportName: 'team_sessions',
    });
  }, [gridRows, t, teamName]);

  return (
    <Paper sx={{p: 5}}>
      <Typography variant="h3" sx={{mb: 2}}>
        {t('pages.teamSessions')}
      </Typography>
      <TeamSessionsFilter
        onDownload={reportData ? onDownload : undefined}
        onReset={onReset}
        onSubmit={onSubmit}
      />
      {loading && <FullScreenLoadingSpinner />}
      {reportData && (
        <Box sx={{height: 700}}>
          <DataGrid rows={gridRows} columns={columns} />
        </Box>
      )}
    </Paper>
  );
};

export default TeamSessionsReport;
